import { FC, useMemo, useState } from "react";
import { Box, Grid, LinearProgress, Stack } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useUserStore } from "../../../../../stores/user.store";
import useEnrollmentSummaryReport from "../../../../../hooks/useEnrollmentSummaryReport";
import BasicKPIComponent from "../../../../Dashboard/BasicKPIComponent";
import DataTable from "../../../../../components/DataTable/DataTable";
import { DataTableCell } from "../../../../../components/DataTable/dataTable.interface";
import ReportFiltersBar, {
	ReportFilters,
	toApiDate,
} from "../../components/ReportFiltersBar";
import { useParticipantColumn } from "../../useParticipantColumn";
import StatusChip from "./StatusChip";
import ByOrgUnitChart from "./ByOrgUnitChart";

interface Filters extends ReportFilters {
	pageNumber: number;
	pageSize: number;
}

dayjs.extend(utc);

// SIMS-8895 (6): dates read as `01 Apr 2024`, not `2024-04-01`. A dayjs token
// rather than a locale-dependent format, so the day/month order is fixed by the
// spec instead of by the viewer's locale.
const REPORT_DATE_FORMAT = "DD MMM YYYY";

/**
 * An enrollment start date, rendered as the calendar day it is.
 *
 * Read in UTC on purpose. The API returns the day at UTC midnight
 * (`2024-04-01T00:00:00.000Z`), so formatting it in the viewer's zone moves it
 * to the previous evening for anyone west of UTC — a Lima or Montana reader saw
 * `31 Mar 2024` for an enrollment that started on the 1st. The old
 * `YYYY-MM-DD` rendering had the same shift; it is corrected here rather than
 * carried into the new format.
 */
const formatStartDate = (value: string | null): string =>
	value ? dayjs.utc(value).format(REPORT_DATE_FORMAT) : "—";

/**
 * The table's columns. Three of this ticket's defects live here.
 *
 * **(4) No Enrollment ID column.** It used to render `enrollmentId.slice(-6)`,
 * a truncation that read as a cropped column rather than a deliberate one, and
 * the id is of no use to the reader. The row object drops the field too —
 * DataTable renders body cells by walking the row's keys, so a leftover key
 * with no head cell shifts every later column out from under its header.
 *
 * **(3) Every text column states `align: "left"`.** DataTableHead defaults a
 * header with no `align` to `textAlign: "right"` when the column is not
 * sortable, while DataTable's body cell falls through to MUI's default (left).
 * Every column here is non-sortable, so each header sat to the right of its
 * data. Fixed at the declaration rather than in DataTableHead's default, which
 * ~59 non-sortable columns across 12 other screens still depend on.
 *
 * **(5) The participant header is the account's own term**, so the columns are
 * built from an argument instead of being a module constant.
 */
const buildHeadCells = (participantLabel: string) => [
	{
		id: "participantName",
		label: participantLabel,
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "orgUnit",
		label: "Org unit",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "researcher",
		label: "Researcher",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "startDate",
		label: "Start date",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "day",
		label: "Day",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		id: "status",
		label: "Status",
		isSortable: false,
		disablePadding: false,
		align: "left" as const,
	},
	{
		// Right-aligned on purpose — a numeric column, and its header already
		// agreed with its data before this ticket.
		id: "completionPct",
		label: "Completion %",
		isSortable: false,
		disablePadding: false,
		align: "right" as const,
	},
];

interface EnrollmentSummaryTabProps {
	programId: string;
}

const EnrollmentSummaryTab: FC<EnrollmentSummaryTabProps> = ({ programId }) => {
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const participant = useParticipantColumn();
	const headCells = useMemo(
		() => buildHeadCells(participant.label),
		[participant.label],
	);

	const [filters, setFilters] = useState<Filters>({
		orgUnitId: "",
		researcherId: "",
		startDate: null,
		endDate: null,
		pageNumber: 0,
		pageSize: 10,
	});

	const { data, isLoading, isFetching } = useEnrollmentSummaryReport({
		accountId,
		programId,
		orgUnitId: filters.orgUnitId || undefined,
		researcherId: filters.researcherId || undefined,
		startDate: toApiDate(filters.startDate),
		endDate: toApiDate(filters.endDate),
		pageNumber: filters.pageNumber,
		pageSize: filters.pageSize,
	});

	const rows = useMemo<DataTableCell[]>(() => {
		return (data?.enrollments ?? []).map((e, idx) => ({
			id: idx,
			// SIMS-8895 (5): name or CIN per the account setting, empty when neither.
			participantName: participant.display(e.participantName, e.participantCin),
			orgUnit: e.orgUnit,
			researcher: e.researcher,
			// SIMS-8895 (6): `01 Apr 2024`, not `2024-04-01`.
			startDate: formatStartDate(e.startDate),
			day: `${e.day}/${e.totalDays}`,
			status: { id: e.enrollmentId, node: <StatusChip status={e.status} /> },
			completionPct: `${e.completionPct}%`,
		}));
	}, [data, participant]);

	return (
		<Stack spacing={3}>
			<ReportFiltersBar
				idPrefix="enrollment-summary"
				filters={filters}
				onChange={(next) =>
					setFilters((p) => ({ ...p, ...next, pageNumber: 0 }))
				}
				availableOrgUnits={data?.availableOrgUnits ?? []}
				availableResearchers={data?.availableResearchers ?? []}
			/>

			{(isLoading || isFetching) && <LinearProgress />}

			{/* KPI cards */}
			<Grid container spacing={2}>
				<BasicKPIComponent
					title="Total enrolled"
					content={data?.kpis.totalEnrolled ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Active"
					content={data?.kpis.active ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Completed"
					content={data?.kpis.completed ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Disenrolled"
					content={data?.kpis.disenrolled ?? 0}
					footer={{ hidden: true }}
				/>
				<BasicKPIComponent
					title="Avg completion (days)"
					content={data?.kpis.avgCompletionDays ?? 0}
					footer={{ hidden: true }}
				/>
			</Grid>

			{/* Stacked bar chart */}
			<ByOrgUnitChart data={data?.byOrgUnit ?? []} isLoading={isLoading} />

			{/* Enrollment list */}
			<Box>
				<DataTable
					data={rows}
					rowsPerPage={filters.pageSize}
					order="desc"
					orderBy="startDate"
					page={filters.pageNumber}
					headCells={headCells}
					total={data?.pagination.total ?? 0}
					isLoading={isLoading || isFetching}
					messageEmptyData="No enrollments match the selected filters"
					onChangePage={(_event, newPage) => {
						setFilters((p) => ({ ...p, pageNumber: newPage }));
					}}
					onChangeSize={(event) => {
						setFilters((p) => ({
							...p,
							pageNumber: 0,
							pageSize: Number(event.target.value),
						}));
					}}
				/>
			</Box>
		</Stack>
	);
};

export default EnrollmentSummaryTab;
